import { CLASS_NAME } from '../../../constants/class-names'
import { ATTR_NAME } from '../../../constants/attr-name'
import {
    AriaAttr,
    EventType,
    HtmlAttr,
    KeyType,
    Position
} from 'constants/enums'
import { Nullable } from 'constants/types'
import { HeaderOffsetService } from 'services/header/header-offset.service'
import { ComponentBase } from 'utils/abstract-classes/component-base'
import { IShareData, nativeAPIShare } from 'utils/system/navigator-share'
import { ShareButtonStore } from './store'
import { logger } from 'utils/system/logger'
import { isTablet, isMobile } from 'utils/system/device'

const { shareButton: classNames } = CLASS_NAME
const { shareButton: attrName } = ATTR_NAME.main

export class ShareButtonComponent extends ComponentBase {
    public data: IShareData = {
        title: '',
        url: window.location.href
    }
    public readonly headerOffSrv: HeaderOffsetService
    private position: Position = Position.BOTTOM
    // eslint-disable-next-line no-magic-numbers
    private readonly gutter = 12
    // eslint-disable-next-line no-magic-numbers
    private readonly scrollTimeout = 60
    private readonly button: HTMLButtonElement
    private readonly modal: HTMLDivElement
    private readonly close: HTMLButtonElement
    private readonly title: Nullable<HTMLHeadingElement>
    private readonly lastItem: HTMLElement
    private readonly modalArrow: Nullable<HTMLSpanElement>
    private readonly isHeaderBtn: boolean

    /**
     * Initializes the share button.
     * @param {HTMLElement} el
     */
    public constructor(container: HTMLElement) {
        super()

        ShareButtonStore.getInstance().add(this)

        this.position = (container.getAttribute(attrName.position) ||
            Position.BOTTOM) as Position

        this.isHeaderBtn = container.hasAttribute(ATTR_NAME.header.share._)

        const button = this.getElement<HTMLButtonElement>(
            `.${classNames._}`,
            container
        )
        const modal = this.getElement<HTMLDivElement>(
            `.${classNames.modal}`,
            container
        )
        const close = this.getElement<HTMLButtonElement>(
            `.${classNames.modalClose}`,
            container
        )
        const lastItem = this.getElement(
            `.${classNames.modalItem}:last-child`,
            container
        )

        if (!button || !modal || !close || !lastItem) {
            // modal elements missing, quit
            return
        }

        this.button = button
        this.modal = modal
        this.close = close
        this.lastItem = lastItem
        this.title = this.getElement(`.${classNames.modalTitle}`, container)
        this.modalArrow = this.getElement<HTMLSpanElement>(
            `.${classNames.modalArrow}`,
            container
        )

        this.data.title = this.title ? this.title.textContent || '' : ''

        this.headerOffSrv = new HeaderOffsetService(this.modal)

        // * attach events
        // Open close modal
        if (this.button) {
            this.button.addEventListener(
                EventType.CLICK,
                async (event: Event) => {
                    // stop default behaviour to check for Web Share Api
                    event.stopPropagation()
                    event.preventDefault()
                    this.tryNativeShare().catch((e) => logger('error', e))
                }
            )
        }

        // Close button
        if (this.close) {
            this.close.addEventListener(EventType.CLICK, () => {
                this.closeModal()
            })
        }

        // close modal on tab away from last item
        if (this.lastItem) {
            this.lastItem.addEventListener(EventType.FOCUS_OUT, () =>
                this.closeModal()
            )
        }

        // Close modal on ESC
        document.addEventListener(EventType.KEYDOWN, (e: KeyboardEvent) => {
            if (e.key === KeyType.ESC || e.key === KeyType.ESC_IE) {
                this.closeModal()
            }
        })

        // * when on the non-full screen version of the share modal
        // scrolling should close the modal
        // reduce the amount this gets fired with timeout threshold
        let isScrolling: NodeJS.Timeout
        document.addEventListener('scroll', () => {
            // Clear our timeout throughout the scroll
            window.clearTimeout(isScrolling)

            // Set a timeout to run after scrolling ends
            isScrolling = setTimeout(() => {
                // Close modal if open on Scroll
                if (this.modal.classList.contains(classNames.modalIsOpen)) {
                    this.closeModal()
                }
            }, this.scrollTimeout)
        })

        // Close modal on click outside modal
        window.addEventListener(EventType.CLICK, (e: Event) => {
            const target = e.target as Element
            if (target && this.modal && this.modal.contains(target)) {
                return
            }
            this.closeModal()
        })
    }

    /**
     * Try using native share API or open fallback modal
     */
    public async tryNativeShare(): Promise<void> {
        // Web Share Api, allowed to try opening native sharing menus
        const nativeShareExists = await nativeAPIShare(this.data)
        // otherwise open the modal
        if (!nativeShareExists) {
            ShareButtonStore.getInstance().close()
            this.openModal()
        }
    }

    /**
     * Open modal by adding class, toggle aria-expanded, and
     * setting position based on breakpoint and position attribute
     */
    public openModal(): void {
        this.modal.classList.add(classNames.modalIsOpen)
        this.button.setAttribute(AriaAttr.EXPANDED, 'true')
        if (isTablet() && isMobile()) {
            this.modal.removeAttribute(HtmlAttr.STYLE)
            if (this.modalArrow) this.modalArrow.removeAttribute(HtmlAttr.STYLE)
            this.headerOffSrv.setStyleTop()
        } else {
            this.positionModal()
        }
    }

    /**
     * Close modal by removing class and toggle aria-expanded
     */
    public closeModal(): void {
        this.modal.classList.remove(classNames.modalIsOpen)
        this.button.setAttribute(AriaAttr.EXPANDED, 'false')
    }

    /**
     * Position Modal using button and modal bounding client rect
     * Contain modal within the viewport using total sticky header height
     * and window inner height and width
     * Position modal arrow relative to modal
     */
    public positionModal(): void {
        const totalHeaderOffset = this.headerOffSrv.totalHeaderHeight
        const buttonRect = this.button.getBoundingClientRect()
        const modalRect = this.modal.getBoundingClientRect()
        // eslint-disable-next-line no-magic-numbers
        const relativeArrowEdge = this.gutter * 3.5 - 1

        let top = buttonRect.y + this.gutter,
            left = buttonRect.x,
            arrowTop = -relativeArrowEdge,
            arrowLeft = 0

        // common positions
        switch (this.position) {
            case Position.LEFT:
            case Position.RIGHT:
                top =
                    buttonRect.y +
                    buttonRect.height -
                    this.gutter -
                    modalRect.height / 2
                arrowTop = modalRect.height / 2 - this.gutter * 2
                break
            case Position.TOP:
            case Position.BOTTOM:
            default:
                left = buttonRect.x - modalRect.width / 2
                arrowLeft = modalRect.width / 2 - this.gutter * 2
                break
        }

        switch (this.position) {
            case Position.LEFT:
                left = buttonRect.x - this.gutter - modalRect.width
                arrowLeft =
                    modalRect.width - relativeArrowEdge + this.gutter / 2 + 2
                break
            case Position.RIGHT:
                left = buttonRect.right + this.gutter
                arrowLeft = -(relativeArrowEdge + 1)
                break
            case Position.TOP:
                top = buttonRect.y - this.gutter - modalRect.height
                arrowTop = modalRect.height - this.gutter * 2 - 1
                break
            case Position.BOTTOM:
            default:
                top = buttonRect.bottom + this.gutter
                arrowTop = -(relativeArrowEdge - this.gutter / 2)
                break
        }

        // make sure calculated results are not outside the viewport
        if (left < 0) {
            left = 0
        } else if (left + modalRect.width > window.innerWidth) {
            left = window.innerWidth - modalRect.width
        }

        if (this.isHeaderBtn) {
            top = buttonRect.y
        } else if (top < totalHeaderOffset) {
            top = totalHeaderOffset
        } else if (top + modalRect.height > window.innerHeight) {
            top = window.innerHeight - modalRect.height
        }

        // set styles
        if (this.modalArrow) {
            this.modalArrow.setAttribute(
                HtmlAttr.STYLE,
                `left:${arrowLeft}px;top:${arrowTop}px`
            )
        }

        this.modal.setAttribute(HtmlAttr.STYLE, `left:${left}px;top:${top}px`)
    }
}
